/**
 * Phase 4 slice 3 — NCR detail. View + investigate (root cause), manage
 * corrective + preventive CAPA actions, record verification, and close
 * (approve-gated).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle2, Loader2, FileDown } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import AuthImage from '../../components/safety/AuthImage';
import { apiGet, apiPut, apiPost, apiDownload } from '../../lib/api';
import { toast } from 'sonner';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
import { formatDateTime as fmt } from '../../lib/format';

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800 whitespace-pre-wrap">{value || '—'}</div>
  </div>
);

const blankAction = { description: '', owner: '', due_date: '', done: false };

const ActionEditor = ({ title, action, onChange, testid }) => (
  <div className="border border-slate-200 rounded-sm p-3" data-testid={testid}>
    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <textarea value={action.description} onChange={(e) => onChange({ ...action, description: e.target.value })} rows={2} placeholder="What will be done" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2" data-testid={`${testid}-desc`} />
    <div className="grid grid-cols-2 gap-2 mb-2">
      <input value={action.owner} onChange={(e) => onChange({ ...action, owner: e.target.value })} placeholder="Owner" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid={`${testid}-owner`} />
      <input type="date" value={(action.due_date || '').slice(0, 10)} onChange={(e) => onChange({ ...action, due_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid={`${testid}-due`} />
    </div>
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={!!action.done} onChange={(e) => onChange({ ...action, done: e.target.checked })} data-testid={`${testid}-done`} /> Action completed
    </label>
  </div>
);

export default function NCRDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [rootCause, setRootCause] = useState('');
  const [corrective, setCorrective] = useState(blankAction);
  const [preventive, setPreventive] = useState(blankAction);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = () => apiGet(`/quality/ncrs/${id}`).then((d) => {
    setDoc(d);
    setRootCause(d.root_cause || '');
    setCorrective({ ...blankAction, ...(d.corrective_action || {}) });
    setPreventive({ ...blankAction, ...(d.preventive_action || {}) });
  }).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveCapa = async () => {
    setSaving(true);
    try {
      await apiPut(`/quality/ncrs/${id}`, {
        root_cause: rootCause,
        corrective_action: corrective.description || corrective.owner ? corrective : null,
        preventive_action: preventive.description || preventive.owner ? preventive : null,
        status: 'capa_pending',
      });
      toast.success('Investigation & CAPA saved');
      load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'No permission to update.' : 'Failed to save');
    } finally { setSaving(false); }
  };

  const close = async (status) => {
    setClosing(true);
    try {
      await apiPost(`/quality/ncrs/${id}/close`, { closure_notes: notes, status });
      toast.success(status === 'waived' ? 'NCR waived' : 'NCR closed');
      load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'You do not have permission to close NCRs.' : 'Failed to close');
    } finally { setClosing(false); }
  };

  if (!doc) return <div className="text-center text-xs text-slate-400 py-16 uppercase tracking-widest font-display">Loading…</div>;
  const isClosed = doc.status === 'closed' || doc.status === 'waived';

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/app/quality/ncrs')} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3" data-testid="ncr-back"><ArrowLeft size={14} /> Back to NCRs</button>
      <PageHeader overline={`${doc.ncr_no} · ${doc.category}`} title={doc.title}
        actions={(
          <div className="flex items-center gap-2">
            {isClosed && (
              <button onClick={() => apiDownload(`/quality/ncrs/${id}/closure-report.pdf`, `NCR_${(doc.ncr_no || id).replace(/\//g, '-')}.pdf`).catch(() => toast.error('PDF failed'))}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="ncr-pdf-btn">
                <FileDown size={13} /> Closure Report
              </button>
            )}
            <span className={`tag border ${SEV_TONE[doc.severity]}`}>{doc.severity}</span>
          </div>
        )} />

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="ncr-detail">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Status" value={(doc.status || '').replace('_', ' ')} />
          <Field label="Source" value={doc.source} />
          <Field label="Location" value={doc.location_on_site} />
          <Field label="Raised By" value={`${doc.raised_by}`} />
        </div>
        <Field label="Description" value={doc.description} />
        {doc.immediate_containment && <Field label="Immediate Containment" value={doc.immediate_containment} />}
        {(doc.contributing_factors || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Contributing factors</div>
            <div className="flex flex-wrap gap-1">
              {doc.contributing_factors.map((f, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{f}</span>)}
            </div>
          </div>
        )}
        {doc.source === 'Cube Test' && doc.source_ref_id && (
          <Link to={`/app/quality/cube-tests/${doc.source_ref_id}`} className="inline-block text-xs font-display font-bold text-[#3b4872] underline" data-testid="ncr-source-link">View source cube test →</Link>
        )}

        {(doc.photos || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Photos ({doc.photos.length})</div>
            <div className="flex flex-wrap gap-2">
              {doc.photos.map((p) => <AuthImage key={p.id} src={p.image_url} alt="ncr" className="w-24 h-24 object-cover rounded-sm border border-slate-200" />)}
            </div>
          </div>
        )}

        {/* Investigation + CAPA */}
        {!isClosed && (
          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Root cause</div>
              <textarea value={rootCause} onChange={(e) => setRootCause(e.target.value)} rows={2} placeholder="Why did it happen?" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ncr-root-cause" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionEditor title="Corrective Action" action={corrective} onChange={setCorrective} testid="ncr-corrective" />
              <ActionEditor title="Preventive Action" action={preventive} onChange={setPreventive} testid="ncr-preventive" />
            </div>
            <button onClick={saveCapa} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] disabled:opacity-60" data-testid="ncr-save-capa">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Investigation & CAPA
            </button>
          </div>
        )}

        {/* Close */}
        {isClosed ? (
          <div className="border-t border-slate-200 pt-4">
            <Field label="Closure" value={`${(doc.status || '').toUpperCase()} by ${doc.closed_by || '—'} · ${fmt(doc.closed_at)}`} />
            {doc.closure_notes && <div className="mt-2"><Field label="Closure Notes" value={doc.closure_notes} /></div>}
          </div>
        ) : (
          <div className="border-t border-slate-200 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Close this NCR</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Closure notes / verification summary" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2" data-testid="ncr-closure-notes" />
            <div className="flex gap-2">
              <button onClick={() => close('closed')} disabled={closing} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="ncr-close-btn">
                {closing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Close NCR
              </button>
              <button onClick={() => close('waived')} disabled={closing} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-60" data-testid="ncr-waive-btn">
                Waive
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
